import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplyIcon from '@mui/icons-material/Reply';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ErrorIcon from '@mui/icons-material/Error';
import { useEmail } from '../../contexts/EmailContext';
import type { Email } from '../../api/email_api';

interface EmailCardProps {
  requestUuid: string;
  onOpenComposer: (replyToEmail?: Email) => void;
  canWork: boolean;
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'delivered':
      return <DoneAllIcon fontSize="small" color="success" />;
    case 'opened':
      return <MarkEmailReadIcon fontSize="small" color="primary" />;
    case 'clicked':
      return <MarkEmailReadIcon fontSize="small" color="secondary" />;
    case 'bounced':
    case 'dropped':
      return <ErrorIcon fontSize="small" color="error" />;
    default:
      return <SendIcon fontSize="small" color="action" />;
  }
};

const getStatusColor = (
  status: string
): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' => {
  switch (status) {
    case 'delivered':
      return 'success';
    case 'opened':
    case 'clicked':
      return 'primary';
    case 'bounced':
    case 'dropped':
      return 'error';
    default:
      return 'default';
  }
};

export const EmailCard = observer(function EmailCard({
  requestUuid,
  onOpenComposer,
  canWork,
}: EmailCardProps) {
  const {
    loading,
    getEmailsByAssociation,
    fetchEmailsByAssociation,
    resendEmail,
  } = useEmail();

  const [expandedEmail, setExpandedEmail] = useState<string | false>(false);
  const [resending, setResending] = useState<string | null>(null);

  const emails = getEmailsByAssociation('request', requestUuid);

  useEffect(() => {
    if (requestUuid) {
      fetchEmailsByAssociation('request', requestUuid);
    }
  }, [requestUuid, fetchEmailsByAssociation]);

  const handleAccordionChange =
    (emailKey: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedEmail(isExpanded ? emailKey : false);
    };

  const handleResend = async (email: Email) => {
    setResending(email.uuid);
    try {
      await resendEmail(email.uuid);
    } catch (err) {
      console.error('Resend failed:', err);
    } finally {
      setResending(null);
    }
  };

  const renderEmail = (email: Email) => {
    return (
      <Accordion
        key={email.key}
        expanded={expandedEmail === email.key}
        onChange={handleAccordionChange(email.key)}
        disableGutters
        elevation={0}
        sx={{ '&:before': { display: 'none' } }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ px: 1, minHeight: 56 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%', overflow: 'hidden' }}>
            {getStatusIcon(email.status)}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap fontWeight={500}>
                {email.subject || '(No subject)'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                To: {email.to_email}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {new Date(email.date_created).toLocaleString()}
              </Typography>
            </Box>
            <Chip
              label={email.status}
              size="small"
              color={getStatusColor(email.status)}
              sx={{ fontSize: '0.65rem', flexShrink: 0 }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1, py: 0, pb: 1 }}>
          <List dense disablePadding>
            <ListItem disableGutters>
              <ListItemText
                primary="From"
                secondary={email.from_email}
                primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                secondaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="To"
                secondary={email.to_email}
                primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                secondaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            {email.content && (
              <ListItem disableGutters>
                <ListItemText
                  primary="Content"
                  secondary={
                    email.content.length > 200
                      ? email.content.slice(0, 200) + '...'
                      : email.content
                  }
                  primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                  secondaryTypographyProps={{
                    variant: 'body2',
                    sx: { whiteSpace: 'pre-wrap' },
                  }}
                />
              </ListItem>
            )}
            {email.files && email.files.length > 0 && (
              <ListItem disableGutters>
                <ListItemText
                  primary="Attachments"
                  secondary={`${email.files.length} file(s)`}
                  primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            )}
          </List>
          {canWork && (
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => onOpenComposer(email)}
                title="Reply"
              >
                <ReplyIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleResend(email)}
                disabled={resending === email.uuid}
                title="Resend"
              >
                {resending === email.uuid ? (
                  <CircularProgress size={16} />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="primary" />
            <Typography variant="h6">Emails</Typography>
            {loading && <CircularProgress size={16} />}
          </Box>
        </Box>

        {/* Send Email Button */}
        {canWork && (
          <Button
            variant="outlined"
            startIcon={<SendIcon />}
            onClick={() => onOpenComposer()}
            fullWidth
            sx={{ mb: 2 }}
          >
            Send Email
          </Button>
        )}

        {/* Email History */}
        {emails.length > 0 ? (
          <Box sx={{ mx: -1 }}>{emails.map(renderEmail)}</Box>
        ) : !loading ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            No emails sent yet
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
});
